import { normalizeHostname, type Branding, type DomainResolution, type ProductKey } from '@traceio-org/platform-contracts';

/** Hosts served as Trace's own sign-in without asking platform-api (local development). */
const TRACE_LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

export function isTraceLocalHost(hostname: string): boolean {
  return TRACE_LOCAL_HOSTS.has(hostname.toLowerCase());
}

/**
 * What the hub knows about the address it is served on.
 * - trace: Trace's own sign-in (optionally pinned to one product).
 * - customer-employee: a verified customer domain for one employee product, with its branding.
 * - customer-portal: a verified customer portal domain, with the provider's branding.
 * - unknown: unknown, unverified or unresolvable. No sign-in form is ever shown on it.
 */
export type HostState =
  | { kind: 'trace'; forcedProduct: ProductKey | null }
  | {
      kind: 'customer-employee';
      host: string;
      forcedProduct: ProductKey;
      organizationId: string;
      branding: Branding;
    }
  | {
      kind: 'customer-portal';
      host: string;
      forcedProduct: 'portal';
      organizationId: string;
      branding: Branding;
      portalName: string;
    }
  | { kind: 'unknown'; reason: 'not_set_up' | 'unreachable' };

function normalizedHost(host: string): string {
  const result = normalizeHostname(host);
  return result.ok ? result.hostname : host.toLowerCase();
}

export function hostStateFromResolution(resolution: DomainResolution): HostState {
  switch (resolution.kind) {
    case 'trace':
      return { kind: 'trace', forcedProduct: resolution.applicationKey === 'hub' ? null : resolution.applicationKey };
    case 'employee-app':
      return {
        kind: 'customer-employee',
        host: normalizedHost(resolution.host),
        forcedProduct: resolution.applicationKey,
        organizationId: resolution.organization.id,
        branding: resolution.organization.branding,
      };
    case 'customer-portal':
      return {
        kind: 'customer-portal',
        host: normalizedHost(resolution.host),
        forcedProduct: 'portal',
        organizationId: resolution.organization.id,
        branding: resolution.organization.branding,
        portalName: resolution.portal.displayName,
      };
  }
}

/** The product a host pins, or null when the route decides. */
export function forcedProductOf(host: HostState): ProductKey | null {
  return host.kind === 'unknown' ? null : host.forcedProduct;
}
